/**
 * @author Mygi
 */

import { Ajax } from '@shared/Ajax';

import * as React from 'react';
import { FilesDownloadDialog } from './FilesDownloadDialog';
import { downloadURL } from '@shared/utils/utils';
import { observer } from 'mobx-react';
import { action, makeObservable, observable } from 'mobx';


enum DownloadState {
    INIT,
    IN_QUEUE,
    PROCESSING,
    DONE,
    ERROR
}


/**
 * Supports json from the following API endpoints:
 *
 * api/1/zip/queue_files
 * api/1/zip/queue_images
 * api/1/zip/queue_project
 */
@observer
export class FilesDownload extends React.Component<{
    uuid: string,
}, {}> {

    @observable dlState: DownloadState = DownloadState.INIT;
    @observable queuePosition = 0;
    @observable queueTotal = 0;
    @observable countdown = 0;
    @observable ready = 0;
    @observable total = 0;
    @observable emailError = null;

    COUNTDOWN_MAX = 15;
    isOpen = true;
    start;
    url;
    errorMessageKey;
    errorMessageParams;

    constructor(props) {
        super(props);

        makeObservable(this);

        this.start = Date.now();
        setTimeout(() => this.checkStatus(), 100);
    }

    checkStatus() {
        if (this.isOpen) {
            this.getDownloadStatus(this.props.uuid).done(({ done, ready, total, positionInQueue, totalInQueue, errorMessage, errorMessageParams, url }) => {
                this.ready = ready;
                this.total = total;
                this.queuePosition = positionInQueue;
                this.queueTotal = totalInQueue;

                if (errorMessage) {
                    this.errorMessageKey = errorMessage;
                    this.errorMessageParams = errorMessageParams;
                    this.dlState = DownloadState.ERROR;
                }
                else if (done) {
                    downloadURL(url);
                    this.url = url;
                    this.dlState = DownloadState.DONE;
                }
                else {
                    switch (this.dlState) {
                        case DownloadState.INIT: {
                            this.dlState = DownloadState.IN_QUEUE;
                            // fall through
                        }
                        case DownloadState.IN_QUEUE: {
                            if (positionInQueue == totalInQueue) {
                                this.dlState = DownloadState.PROCESSING;
                            }
                            break;
                        }
                        case DownloadState.PROCESSING: {
                            // Increase progress % every 1.5 seconds for COUNTDOWN_MAX seconds while we are waiting for the background zip task to start
                            const now = Date.now();
                            this.countdown = Math.min(Math.floor((now - this.start) / 1500), this.COUNTDOWN_MAX);

                            break;
                        }
                    }

                    setTimeout(() => this.checkStatus(), 250);
                }
            });
        }
    }

    @action.bound
    sendEmailAndClose(e) {
        if (e) e.preventDefault();

        this.postSendEmailQuery(this.props.uuid).done(() => {
            FilesDownloadDialog.close();
        }).fail(() => {
            this.emailError = kuura.getMessage('js_files.send_email_error');
        });
    }

    getDownloadStatus(uuid: string): JQueryPromise<any> {
        return Ajax({
            url: `api/1/zip/${uuid}/status.json`,
            method: 'GET',
        });
    }

    postSendEmailQuery(uuid: string): JQueryPromise<any> {
        return Ajax({
            url: 'api/1/zip/send-email-when-done',
            data: {
                uuid,
            },
            method: 'POST',
        });
    }

    render() {
        let emailStatusContent;
        let dynamicContent;

        switch (this.dlState) {
            case DownloadState.INIT: {
                dynamicContent = (
                    <div className='card padded'>
                        <div>
                            {kuura.getMessage('js_files.status')}:
                            {kuura.getMessage('js_files.initializing')}
                        </div>

                        <div>
                        </div>
                    </div>
                );
                break;
            }
            case DownloadState.IN_QUEUE: {
                dynamicContent = (
                    <div className='card padded'>
                        <div>
                            {kuura.getMessage('js_files.status')}:
                            {kuura.getMessage('js_files.in_queue')} {this.queuePosition} / {this.queueTotal}
                        </div>

                        <div>
                        </div>
                    </div>
                );
                break;
            }
            case DownloadState.PROCESSING: {
                dynamicContent = (
                    <div className='card padded'>
                        <div>
                            {kuura.getMessage('js_files.status')}:
                            {kuura.getMessage('js_files.processing')} {Math.max(this.countdown, Math.round(this.ready / this.total * 100))}%
                        </div>

                        <div>
                        </div>
                    </div>
                );
                break;
            }
            case DownloadState.DONE: {
                dynamicContent = (
                    <div className='card padded'>
                        <div>
                            {kuura.getMessage('js_files.status')}: {kuura.getMessage('js_files.done')}
                        </div>

                        <div>
                            <a href={this.url} target='_blank'>{kuura.getMessage('js_files.download_manually')}</a>
                        </div>
                    </div>
                );
                break;
            }
            case DownloadState.ERROR: {
                dynamicContent = (
                    <div className='card padded'>
                        {kuura.getMessage(this.errorMessageKey, this.errorMessageParams)}
                    </div>
                );
                break;
            }
        }

        if (this.emailError) {
            emailStatusContent = (
                <div className='card padded'>
                    {this.emailError}
                </div>
            );
        }

        return (
            <div id='images-download'>
                <div className='card padded'>
                    <div className='hidden'>
                        {this.props.uuid}
                    </div>
                    {kuura.getMessage('js_files.zip_description')}
                    <a href='#' onClick={this.sendEmailAndClose} className='clickable'>{kuura.getMessage('js_files.close_and_send_email_when_done')}</a>
                </div>
                {emailStatusContent}
                {dynamicContent}
            </div>
        );
    }

    componentWillUnmount() {
        this.isOpen = false;
    }
};
